import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import {
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  FiltersTabs,
  IconButton,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '../components/ui';
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  KeyIcon,
  LockClosedIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type FilterKey = 'all' | 'active' | 'inactive';

interface Operator {
  id: number;
  username: string;
  is_active: boolean;
  created_at: string;
}

interface CreateOperatorData {
  username: string;
  password: string;
}

const filterLabels: Record<FilterKey, string> = {
  all: 'Все',
  active: 'Активные',
  inactive: 'Неактивные',
};

const Operators: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState<CreateOperatorData>({ username: '', password: '' });
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const location = useLocation();
  const navigateRouter = useNavigate();

  const operatorsQuery = useQuery<Operator[]>({
    queryKey: ['operators'],
    queryFn: async () => {
      const { data } = await api.get('/admin/operators');
      return (data || []) as Operator[];
    },
    enabled: user?.role === 'admin',
  });

  const operators = operatorsQuery.data ?? [];
  const isLoading = operatorsQuery.isLoading;

  useEffect(() => {
    const state = location.state as { initialSearch?: string } | null;
    if (!state) return;
    if (state.initialSearch) {
      setSearch(state.initialSearch);
    }
    navigateRouter(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const createMutation = useMutation({
    mutationFn: async (payload: CreateOperatorData) => {
      const { data } = await api.post('/admin/operators', payload);
      return data;
    },
    onSuccess: (data) => {
      alert(`Оператор ${data.username} успешно создан`);
      queryClient.invalidateQueries({ queryKey: ['operators'] });
      setShowCreate(false);
      setFormData({ username: '', password: '' });
      setError('');
    },
    onError: (err: any) => {
      setError(err?.response?.data?.error || 'Ошибка создания оператора');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ operatorId, isActive }: { operatorId: number; isActive: boolean }) => {
      await api.put(`/admin/operators/${operatorId}/status`, { isActive });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operators'] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error || 'Ошибка при обновлении статуса');
    },
  });

  const resetMutation = useMutation({
    mutationFn: async ({ operatorId, newPassword }: { operatorId: number; newPassword: string }) => {
      await api.post(`/admin/operators/${operatorId}/reset-password`, { newPassword });
    },
    onSuccess: () => {
      alert('Пароль успешно изменен');
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error || 'Ошибка при сбросе пароля');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (operatorId: number) => {
      await api.delete(`/admin/operators/${operatorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operators'] });
    },
    onError: (err: any) => {
      alert(err?.response?.data?.error || 'Ошибка при удалении оператора');
    },
  });

  const filteredOperators = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return operators.filter((operator) => {
      const matchesFilter =
        filter === 'all' || (filter === 'active' && operator.is_active) || (filter === 'inactive' && !operator.is_active);
      const matchesSearch =
        !normalizedSearch || operator.username.toLowerCase().includes(normalizedSearch) || String(operator.id).includes(normalizedSearch);
      return matchesFilter && matchesSearch;
    });
  }, [operators, filter, search]);

  const filterTabs = useMemo(() => {
    const counts = operators.reduce<Record<FilterKey, number>>(
      (acc, operator) => {
        acc.all += 1;
        if (operator.is_active) acc.active += 1;
        else acc.inactive += 1;
        return acc;
      },
      { all: 0, active: 0, inactive: 0 }
    );
    return (Object.keys(filterLabels) as FilterKey[]).map((key) => ({ key, label: filterLabels[key], badge: counts[key] }));
  }, [operators]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    createMutation.mutate(formData);
  };

  if (user?.role !== 'admin') {
    return <div className="text-center text-red-600">Доступ запрещен. Требуется роль администратора.</div>;
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-slate-900">Операторы магазина</h1>
          <p className="text-sm text-slate-500">Создавайте учётные записи операторов и управляйте их доступом.</p>
        </div>
        <div className="flex items-center gap-2">
          <IconButton variant="ghost" aria-label="Обновить" onClick={() => operatorsQuery.refetch()}>
            <ArrowPathIcon className="h-5 w-5" />
          </IconButton>
          <button
            onClick={() => {
              setShowCreate(true);
              setError('');
            }}
            className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-600"
          >
            <PlusIcon className="h-4 w-4" /> Добавить оператора
          </button>
        </div>
      </div>

      <Card>
        <CardHeader
          className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          title={<span className="text-lg font-semibold text-slate-900">Все операторы</span>}
          actions={<FiltersTabs tabs={filterTabs} value={filter} onChange={(key) => setFilter(key as FilterKey)} />}
          description="Фильтруйте по статусу и используйте поиск для быстрого доступа."
        />
        <CardBody className="space-y-4">
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
            <AdjustmentsHorizontalIcon className="h-5 w-5 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Поиск по логину или ID"
              className="w-full border-none bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>Оператор</TableHeaderCell>
                  <TableHeaderCell>Статус</TableHeaderCell>
                  <TableHeaderCell>Создан</TableHeaderCell>
                  <TableHeaderCell numeric>Действия</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500">
                      Загружаем операторов...
                    </TableCell>
                  </TableRow>
                ) : filteredOperators.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <EmptyState
                        title="Операторов пока нет"
                        description="Создайте первого оператора, чтобы начать делегировать задачи по обработке платежей и выводов."
                        icon={<UserIcon className="h-7 w-7" />}
                        actions={
                          <button
                            onClick={() => setShowCreate(true)}
                            className="rounded-full bg-primary-500 px-3 py-1.5 text-sm font-semibold text-white shadow-soft hover:bg-primary-600"
                          >
                            Создать оператора
                          </button>
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOperators.map((operator) => (
                    <TableRow key={operator.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-50 text-primary-500">
                            {operator.username.slice(0, 2).toUpperCase()}
                          </div>
                          <div className="space-y-1 text-sm">
                            <p className="font-semibold text-slate-900">{operator.username}</p>
                            <p className="text-xs text-slate-500">ID: {operator.id}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge tone={operator.is_active ? 'success' : 'danger'}>
                          {operator.is_active ? 'Активен' : 'Неактивен'}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-slate-600">{new Date(operator.created_at).toLocaleDateString('ru-RU')}</div>
                      </TableCell>
                      <TableCell numeric>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() =>
                              toggleMutation.mutate({ operatorId: operator.id, isActive: !operator.is_active })
                            }
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 transition hover:border-primary-200 hover:text-primary-600"
                          >
                            {operator.is_active ? <LockClosedIcon className="h-4 w-4" /> : <KeyIcon className="h-4 w-4" />}
                            {operator.is_active ? 'Деактивировать' : 'Активировать'}
                          </button>
                          <button
                            onClick={() => {
                              const newPassword = prompt('Введите новый пароль для оператора:');
                              if (!newPassword) return;
                              resetMutation.mutate({ operatorId: operator.id, newPassword });
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-primary-600 transition hover:border-primary-200"
                          >
                            <KeyIcon className="h-4 w-4" /> Сбросить пароль
                          </button>
                          <button
                            onClick={() => {
                              if (confirm('Вы уверены, что хотите удалить этого оператора?')) {
                                deleteMutation.mutate(operator.id);
                              }
                            }}
                            className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-danger transition hover:border-danger/30"
                          >
                            <TrashIcon className="h-4 w-4" /> Удалить
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      {showCreate ? (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-slate-900/40" onClick={() => setShowCreate(false)} />
          <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-lifted">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">Создание оператора</p>
                <h2 className="text-lg font-semibold text-slate-900">Новая учётная запись</h2>
              </div>
              <IconButton aria-label="Закрыть" onClick={() => setShowCreate(false)}>
                <XMarkIcon className="h-5 w-5" />
              </IconButton>
            </div>
            <form className="flex-1 space-y-4 overflow-y-auto px-5 py-6" onSubmit={handleSubmit}>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase text-secondary-500">Логин</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(event) => setFormData((prev) => ({ ...prev, username: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  required
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold uppercase text-secondary-500">Пароль</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(event) => setFormData((prev) => ({ ...prev, password: event.target.value }))}
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  required
                  minLength={6}
                />
                <p className="text-xs text-slate-500">Минимум 6 символов. Пароль можно будет изменить позже.</p>
              </div>

              {error ? <div className="rounded-xl bg-danger/10 px-4 py-3 text-sm text-danger">{error}</div> : null}

              <div className="flex items-center justify-end gap-2 border-t border-slate-200 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreate(false);
                    setError('');
                    setFormData({ username: '', password: '' });
                  }}
                  className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary-200"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? 'Создание...' : 'Создать оператора'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Operators;
